import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  BugReport as BugIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
// ✅ CORRECTION : Import depuis api.service au lieu de api
import { comptesRendusService } from '../../services/api.service';
import axios from 'axios';

const DebugComptesRendus = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    const testResults = {
      timestamp: new Date().toISOString(),
      tests: []
    };

    // Test 1: Vérifier si le serveur répond
    console.log('🧪 Test 1: Connexion au serveur...');
    try {
      const healthCheck = await axios.get('/api/health');
      testResults.tests.push({
        name: 'Connexion serveur',
        status: 'success',
        message: 'Serveur accessible ✓',
        data: healthCheck.data
      });
    } catch (err) {
      testResults.tests.push({
        name: 'Connexion serveur',
        status: 'error',
        message: 'Serveur inaccessible ✗',
        error: err.message
      });
    }

    // Test 2: Appel direct à l'API comptes-rendus
    console.log('🧪 Test 2: Appel API comptes-rendus...');
    try {
      const response = await axios.get('/api/comptes-rendus');
      testResults.tests.push({
        name: 'API comptes-rendus (axios direct)',
        status: 'success',
        message: `Réponse reçue ✓`,
        data: {
          status: response.status,
          statusText: response.statusText,
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          hasSuccess: response.data?.success,
          hasData: response.data?.data,
          dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 
                      Array.isArray(response.data) ? response.data.length : 'N/A',
          fullResponse: response.data
        }
      });
    } catch (err) {
      testResults.tests.push({
        name: 'API comptes-rendus (axios direct)',
        status: 'error',
        message: 'Erreur lors de l\'appel ✗',
        error: {
          message: err.message,
          code: err.code,
          response: err.response?.data
        }
      });
    }

    // Test 3: Appel via le service
    console.log('🧪 Test 3: Appel via comptesRendusService...');
    try {
      const response = await comptesRendusService.getAll();
      testResults.tests.push({
        name: 'API via service',
        status: 'success',
        message: 'Service fonctionnel ✓',
        data: {
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          hasSuccess: response.data?.success,
          hasData: response.data?.data,
          dataLength: Array.isArray(response.data?.data) ? response.data.data.length :
                      Array.isArray(response.data) ? response.data.length : 'N/A',
          fullResponse: response.data
        }
      });
    } catch (err) {
      testResults.tests.push({
        name: 'API via service',
        status: 'error',
        message: 'Erreur dans le service ✗',
        error: {
          message: err.message,
          code: err.code,
          response: err.response?.data
        }
      });
    }

    // Test 4: Vérifier les variables d'environnement
    console.log('🧪 Test 4: Variables d\'environnement...');
    testResults.tests.push({
      name: 'Variables d\'environnement',
      status: 'info',
      message: 'Configuration',
      data: {
        REACT_APP_API_URL: process.env.REACT_APP_API_URL || 'non défini (utilise /api par défaut)',
        NODE_ENV: process.env.NODE_ENV
      }
    });

    console.log('✅ Tous les tests terminés', testResults);
    setResults(testResults);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckIcon />;
      case 'error': return <ErrorIcon />;
      default: return <BugIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BugIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h1">
              🔍 Débogage - Comptes Rendus
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Cet outil vous aide à diagnostiquer les problèmes de chargement des comptes rendus.
            Cliquez sur "Lancer les tests" pour analyser la connexion avec le backend.
          </Alert>

          <Button
            variant="contained"
            onClick={runTests}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <BugIcon />}
            fullWidth
          >
            {loading ? 'Tests en cours...' : 'Lancer les tests de diagnostic'}
          </Button>
        </CardContent>
      </Card>

      {results && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 Résultats des tests
            </Typography>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Exécuté le: {new Date(results.timestamp).toLocaleString('fr-FR')}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {results.tests.map((test, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: test.status === 'error' ? 'error.light' : 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    icon={getStatusIcon(test.status)}
                    label={test.name}
                    color={getStatusColor(test.status)}
                    sx={{ mr: 2 }}
                  />
                  <Typography variant="body2">
                    {test.message}
                  </Typography>
                </Box>

                {test.data && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto' }}>
                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(test.data, null, 2)}
                    </Typography>
                  </Box>
                )}

                {test.error && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1, overflow: 'auto' }}>
                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: 'error.contrastText' }}>
                      {JSON.stringify(test.error, null, 2)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))}

            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Interprétation des résultats:</strong>
              <ul>
                <li>Si "Connexion serveur" est en erreur → Le backend n'est pas démarré</li>
                <li>Si "API comptes-rendus" est en erreur → Problème avec la route /api/comptes-rendus</li>
                <li>Si tous les tests passent mais le composant plante → Vérifiez le format des données</li>
              </ul>
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DebugComptesRendus;